import type { AxiosInstance } from 'axios';

export type WalletBucket = 'CASHBACK' | 'REFUND' | 'REFERRAL' | 'PROMO' | 'GENERIC';
export type WalletCreditStatus = 'ACTIVE' | 'PARTIALLY_USED' | 'FULLY_USED' | 'EXPIRED' | 'REVOKED';
export type PaymentSplitMethod = 'WALLET_ONLY' | 'WALLET_AND_GATEWAY' | 'GATEWAY_ONLY';
export type ReserveType = 'REFUND_RISK' | 'CHARGEBACK' | 'FRAUD_FREEZE';
export type ReserveStatus = 'ACTIVE' | 'RELEASED' | 'FORFEITED';
export type WalletDisputeStatus = 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'REJECTED';

export interface WalletCredit {
  id: string;
  bucket: WalletBucket;
  amountPaise: number;
  remainingPaise: number;
  status: WalletCreditStatus;
  expiresAt: string | null;
  sourceRefType: string | null;
  sourceRefId: string | null;
  note: string | null;
  createdAt: string;
}

export interface WalletBucketBalance {
  bucket: WalletBucket;
  spendablePaise: number;
  expiringSoonPaise: number;
}

export interface CustomerWalletSummary {
  userId: string;
  totalSpendablePaise: number;
  buckets: WalletBucketBalance[];
  recentCredits: WalletCredit[];
}

export interface VendorWalletSummary {
  vendorId: string;
  cashPaise: number;
  heldPaise: number;
  withdrawablePaise: number;
  holdsByType: { type: ReserveType; paise: number }[];
  activeHolds?: {
    id: string;
    type: ReserveType;
    amountPaise: number;
    reason: string | null;
    createdAt: string;
  }[];
}

export interface LedgerEntryInfo {
  id: string;
  transactionId: string;
  accountId: string;
  accountCode: string;
  accountKind: string;
  debitPaise: number;
  creditPaise: number;
  kind: string;
  refType: string | null;
  refId: string | null;
  memo: string | null;
  postedAt: string;
  createdAt: string;
}

export interface PaymentSplitQuote {
  totalPaise: number;
  walletPaise: number;
  gatewayPaise: number;
  method: PaymentSplitMethod;
  walletAllocations: { creditId: string; bucket: WalletBucket; paise: number }[];
}

export interface IssueCreditRequest {
  userId: string;
  bucket: WalletBucket;
  amountPaise: number;
  expiresAt?: string;
  note?: string;
}

export interface CreateReserveHoldRequest {
  vendorId: string;
  type: ReserveType;
  amountPaise: number;
  scheduledReleaseAt?: string;
  reason?: string;
}

export interface ResolveDisputeRequest {
  status: 'RESOLVED' | 'REJECTED';
  resolutionNote: string;
  correctionPaise?: number;
  correctionBucket?: WalletBucket;
}

/**
 * What the wallet can put towards one order, with the reasoning.
 *
 * `usablePaise` is the number to offer. The rest is there so the screen can say
 * why it is not the whole balance — reward money is capped at a share of each
 * order, while a top-up or a refund is the customer's own and is not.
 */
export interface WalletRedemptionQuote {
  totalPaise: number;
  usablePaise: number;
  rewardBalancePaise: number;
  rewardCapPaise: number;
  ownBalancePaise: number;
  maxPercentOfOrder: number;
  enabled: boolean;
}

export type WalletTopupStatus = 'CREATED' | 'CREDITING' | 'PAID' | 'FAILED';

/** Everything the client SDK needs to open the gateway sheet for a top-up. */
export interface WalletTopupInit {
  topupId: string;
  keyId: string | null;
  providerOrderId: string | null;
  amountPaise: number;
  currency: 'INR';
  nextAction: 'OPEN_RAZORPAY_CHECKOUT';
}

export interface WalletTopupResult {
  topupId: string;
  status: WalletTopupStatus;
  creditedPaise: number;
  creditId: string | null;
}

/**
 * One passbook line, already in the customer's point of view: `creditPaise` is
 * money that arrived, `debitPaise` is money that left. The server flips the
 * ledger's accounting sides so no caller has to know it did.
 */
export interface WalletPassbookEntry {
  id: string;
  transactionId: string;
  kind: string;
  memo: string | null;
  debitPaise: number;
  creditPaise: number;
  refType: string | null;
  refId: string | null;
  createdAt: string;
}

export function walletEndpoints(http: AxiosInstance) {
  return {
    // Customer
    mySummary: () => http.get<CustomerWalletSummary>('/wallet/me').then((r) => r.data),
    quote: (body: {
      totalPaise: number;
      maxWalletPaise?: number;
      allowedBuckets?: WalletBucket[];
    }) => http.post<PaymentSplitQuote>('/wallet/me/quote', body).then((r) => r.data),
    /** How much of the balance may go towards an order of this size. */
    redemptionQuote: (totalPaise: number) =>
      http
        .post<WalletRedemptionQuote>('/wallet/me/redemption-quote', { totalPaise })
        .then((r) => r.data),
    /** The passbook: everything that moved in or out of the wallet. */
    transactions: () =>
      http.get<WalletPassbookEntry[]>('/wallet/me/transactions').then((r) => r.data),
    /** Open a top-up. `amountPaise`, like every other amount in this API. */
    addFunds: (amountPaise: number) =>
      http.post<WalletTopupInit>('/wallet/me/topup', { amountPaise }).then((r) => r.data),
    /**
     * Hand back what the gateway told the client. The server confirms it with
     * Razorpay before crediting, so a `CREATED` result is "not yet", not "no".
     */
    verifyAddFunds: (body: {
      razorpayOrderId: string;
      razorpayPaymentId: string;
      razorpaySignature: string;
    }) => http.post<WalletTopupResult>('/wallet/me/topup/verify', body).then((r) => r.data),
    raiseDispute: (body: { disputedTxId: string; reason: string; description?: string }) =>
      http.post('/wallet/me/disputes', body).then((r) => r.data),
    withdraw: (amount: number) =>
      http
        .post<{ success: boolean; debitedPaise: number }>('/wallet/me/withdraw', { amount })
        .then((r) => r.data),

    // Vendor
    vendorMySummary: () => http.get<VendorWalletSummary>('/wallet/vendor/me').then((r) => r.data),
    vendorMyLedger: () =>
      http.get<LedgerEntryInfo[]>('/wallet/vendor/me/ledger').then((r) => r.data),

    // Admin
    adminGetCustomer: (userId: string) =>
      http.get<CustomerWalletSummary>(`/admin/wallet/users/${userId}`).then((r) => r.data),
    adminGetCustomerLedger: (userId: string) =>
      http.get<LedgerEntryInfo[]>(`/admin/wallet/users/${userId}/ledger`).then((r) => r.data),
    adminGetVendor: (vendorId: string) =>
      http.get<VendorWalletSummary>(`/admin/wallet/vendors/${vendorId}`).then((r) => r.data),
    adminGetVendorLedger: (vendorId: string) =>
      http.get<LedgerEntryInfo[]>(`/admin/wallet/vendors/${vendorId}/ledger`).then((r) => r.data),
    expireSweep: () => http.post('/admin/wallet/expire-sweep').then((r) => r.data),
    issueCredit: (body: IssueCreditRequest) =>
      http.post('/admin/wallet/credits', body).then((r) => r.data),
    revokeCredit: (creditId: string, reason: string) =>
      http.post(`/admin/wallet/credits/${creditId}/revoke`, { reason }).then((r) => r.data),
    createReserve: (body: CreateReserveHoldRequest) =>
      http.post('/admin/wallet/reserves', body).then((r) => r.data),
    releaseReserve: (holdId: string, body: { action: 'RELEASE' | 'FORFEIT'; note?: string }) =>
      http.post(`/admin/wallet/reserves/${holdId}/release`, body).then((r) => r.data),
    resolveDispute: (disputeId: string, body: ResolveDisputeRequest) =>
      http.post(`/admin/wallet/disputes/${disputeId}/resolve`, body).then((r) => r.data),
  };
}

export type WalletEndpoints = ReturnType<typeof walletEndpoints>;
